import {
  ChatbotMessage,
  useChatbotSettingsStore,
} from 'store/useChatbotSettingsStore'
import { ChatHeading } from './ChatHeading'
import { ChatInput } from './ChatInput'
import { ChatMessages } from './ChatMessages'
import { PoweredBy } from './PoweredBy'
import { useAIChat } from 'hooks/useAIChat'
import { useState } from 'react'

export const ChatBox = ({
  isIframed = false,
  autoscroll = true,
  includeUserMessage = false,
}) => {
  const classes = isIframed
    ? 'z-50 bg-white grid grid-rows-[auto,1fr,auto] h-full w-full'
    : 'flex flex-1 sm:max-w-sm sm:w-screen flex-col sm:rounded-2xl overflow-hidden sm:shadow-2xl sm:border border-gray-100 sm:h-[700px] z-50 bg-white h-screen sm:max-h-[calc(100vh-2rem)]'

  const { chatbot, messages, setMessages } = useChatbotSettingsStore()
  const { getChatResponse } = useAIChat()
  const [message, setMessage] = useState('')
  const [fetchingAIResponse, setFetchingAIResponse] = useState(false) // Add loading state

  const { isLoading } = useChatbotSettingsStore()

  const vectorSearch = async (messageContent: string) => {
    if (!messageContent) return
    const userMessage: ChatbotMessage = {
      role: 'user',
      content: messageContent,
    }
    setMessages([...messages, userMessage])
    setFetchingAIResponse(true) // Set loading to true when starting the search
    try {
      if (!chatbot.system_message) {
        throw new Error('System message is required')
      }
      if (!chatbot.user_message) {
        throw new Error('User message is required')
      }

      if (!chatbot.id) {
        throw new Error('Chatbot ID is required')
      }

      const result = await getChatResponse({
        system_message: chatbot.system_message,
        user_message: chatbot.user_message,
        query: messageContent,
        submission_ids: Object.keys(chatbot.submissions || {}),
        use_all_uploads: chatbot.use_all_uploads ?? true,
        chatbot_id: chatbot.id,
        conversation_history: messages.map(
          (message) => `${message.role}: ${message.content}\n`
        ),
      })

      const aiMessage: ChatbotMessage = {
        role: 'assistant',
        content: result.chat_response,
        sources: result.page_sections,
      }

      // Add new AI message to the messages array
      setMessages([...messages, userMessage, aiMessage])
      setMessage('') // clear message after getOpenAIResponse has been called
    } catch (error) {
      console.error('Failed to get response:', error)
      setMessages([
        ...messages,
        {
          role: 'assistant',
          content:
            'It looks like we are having trouble connecting to our servers. Please try again later.',
        },
      ])
    } finally {
      setFetchingAIResponse(false) // Set loading to false after the search is complete
    }
  }

  if (isLoading) {
    return <Skeleton />
  }

  return (
    <div className={classes}>
      <ChatHeading isIframed={isIframed} />
      <ChatMessages
        autoscroll={autoscroll}
        vectorSearch={vectorSearch}
        loading={fetchingAIResponse}
      />
      <ChatInput
        vectorSearch={vectorSearch}
        message={message}
        setMessage={setMessage}
      />
      <PoweredBy />
    </div>
  )
}

// skeleton of the div above:
const Skeleton = () => {
  return (
    <div className="z-50 flex h-screen flex-1 animate-pulse flex-col overflow-hidden border-gray-100 bg-white sm:h-[700px] sm:max-h-[calc(100vh-2rem)] sm:w-screen sm:max-w-sm sm:rounded-2xl sm:border sm:shadow-2xl">
      <div className="z-10 border-b border-gray-200 bg-gray-50 py-4 shadow">
        <div className="flex w-full items-center justify-between px-4 py-2">
          <div className="flex items-center">
            <div className="h-10 w-10 rounded-full bg-gray-300"></div>
            <div className="ml-2 flex flex-col">
              <div className="mb-1 h-4 w-24 rounded bg-gray-300"></div>
              <div className="h-3 w-16 rounded bg-gray-300"></div>
            </div>
          </div>
          <div className="h-7 w-7 rounded-full bg-gray-300"></div>
        </div>
      </div>
      <ul className="h-full w-full flex-1 overflow-y-auto bg-[#EAEEF3] p-4">
        <li className="mb-2 flex w-full flex-col">
          <div className="mb-2 h-4 w-32 rounded bg-gray-300"></div>
          <div className="h-8 rounded bg-gray-300"></div>
        </li>
        <li className="mb-2 flex w-full flex-col">
          <div className="h-8 rounded bg-gray-300"></div>
        </li>
      </ul>
      <div className="relative my-2 flex items-center px-4">
        <div className="h-10 w-full rounded bg-gray-300"></div>
      </div>
      <div className="border-t border-gray-100 bg-gray-100 px-4 py-2">
        <div className="mx-auto h-4 w-24 rounded bg-gray-300"></div>
      </div>
    </div>
  )
}
